import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaSearch, FaSync, FaCheckCircle, FaExclamationTriangle, FaFilter } from "react-icons/fa";
import API_BASE_URL from "../../../../config/api";
import { useAuth } from "../../../../context/AuthContext";

function ThrustAreaCoordinator() {
  const { user } = useAuth();
  const [coordinators, setCoordinators] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [thrustAreas, setThrustAreas] = useState([]);
  const [selectedThrustArea, setSelectedThrustArea] = useState("");

  useEffect(() => {
    fetchCoordinators();
    fetchThrustAreas();
  }, []);

  const fetchThrustAreas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/thrust-areas/`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      if (response.data.data) {
        setThrustAreas(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching thrust areas:", error);
    }
  };

  const fetchCoordinators = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/thrustarea-coordinators`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      setCoordinators(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching thrustarea coordinators:", error);
      setError("Failed to load thrustarea coordinators. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncCoordinators = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE_URL}/api/thrustarea-coordinators/sync`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      if (response.status === 200) {
        setSyncSuccess(true);
        setTimeout(() => setSyncSuccess(false), 3000);
        await fetchCoordinators();
      }
    } catch (error) {
      console.error("Error syncing thrustarea coordinators:", error);
      setError("Failed to sync thrustarea coordinators. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoordinatorsByThrustArea = async (thrustArea) => {
    if (!thrustArea) {
      return fetchCoordinators();
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/thrust-areas/${thrustArea}/coordinators`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      setCoordinators(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching thrustarea coordinators by thrust area:", error);
      setError("Failed to load thrustarea coordinators. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleThrustAreaChange = (e) => {
    const thrustArea = e.target.value;
    setSelectedThrustArea(thrustArea);
    fetchCoordinatorsByThrustArea(thrustArea);
  };

  const filteredCoordinators = coordinators.filter(
    (coordinator) =>
      search === "" ||
      (coordinator.name && coordinator.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen p-6 md:p-12 font-sans">
      <div className="max-w-7xl mx-auto">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Thrust Area Coordinator Directory
            </h1>
            <p className="text-gray-500 mt-2">View and sync thrust area coordinators</p>
          </div>
        </header>

        {/* Alerts */}
        {syncSuccess && (
          <div className="mb-6 p-4 rounded-lg flex items-center gap-3 shadow-sm bg-green-50 text-green-700 border border-green-200 animate-fade-in">
            <FaCheckCircle />
            Thrust Area Coordinators successfully synchronized from faculty data!
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-lg flex items-center gap-3 shadow-sm bg-red-50 text-red-700 border border-red-200 animate-fade-in">
            <FaExclamationTriangle />
            {error}
          </div>
        )}

        {/* Actions Bar */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8 flex flex-col lg:flex-row gap-4 justify-between items-center">
          <div className="flex flex-col md:flex-row gap-4 w-full">
            {/* Search */}
            <div className="relative w-full md:max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Coordinator by Name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] transition-all"
              />
            </div>

            {/* Filter */}
            <div className="relative w-full md:max-w-xs">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={selectedThrustArea}
                onChange={handleThrustAreaChange}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#b7202e]/20 focus:border-[#b7202e] bg-white appearance-none cursor-pointer"
              >
                <option value="">All Thrust Areas</option>
                {thrustAreas.map((thrustArea, idx) => (
                  <option key={idx} value={thrustArea.name || thrustArea}>
                    {thrustArea.name} ({thrustArea.department})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={handleSyncCoordinators}
            disabled={loading}
            className="w-full lg:w-auto flex items-center justify-center gap-2 bg-[#b7202e] hover:bg-[#901a24] text-white px-5 py-2.5 rounded-lg transition-colors shadow-md shadow-red-100 font-medium disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <FaSync className={loading ? "animate-spin" : ""} />
            {loading ? "Syncing..." : "Refresh / Sync"}
          </button>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading && !coordinators.length ? (
            <div className="p-12 text-center text-gray-500 flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-[#b7202e] rounded-full animate-spin mb-4"></div>
              <p>Loading coordinators...</p>
            </div>
          ) : filteredCoordinators.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-sm uppercase tracking-wider">
                    <th className="p-4 font-semibold">Coordinator ID</th>
                    <th className="p-4 font-semibold">Name</th>
                    <th className="p-4 font-semibold">Faculty Abr</th>
                    <th className="p-4 font-semibold">Email</th>
                    <th className="p-4 font-semibold">Department</th>
                    <th className="p-4 font-semibold">Designation</th>
                    <th className="p-4 font-semibold">Thrust Area</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCoordinators.map((coordinator) => (
                    <tr key={coordinator._id} className="hover:bg-red-50/30 transition-colors">
                      <td className="p-4 text-gray-700 font-medium">{coordinator.coordinatorId}</td>
                      <td className="p-4 text-gray-800 font-semibold">{coordinator.name}</td>
                      <td className="p-4 text-gray-600">{coordinator.facultyAbr}</td>
                      <td className="p-4 text-gray-500 text-sm">{coordinator.email}</td>
                      <td className="p-4 text-gray-600">
                        <span className="inline-block px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">
                          {coordinator.department}
                        </span>
                      </td>
                      <td className="p-4 text-gray-600">{coordinator.designation}</td>
                      <td className="p-4 text-gray-600">
                        <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          {coordinator.thrustArea}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500 bg-gray-50">
              <FaSearch className="text-4xl mx-auto mb-3 text-gray-300" />
              <p>No thrust area coordinators found. Sync from faculty to add thrust area coordinators.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ThrustAreaCoordinator;